import React, {useRef, useEffect, useState, useContext} from 'react';
import { useNavigate  } from "react-router";
import ModalWrapper from  '@/components/modals/ModalWrapper';
import useHTTP from  '@/components/hooks/useHTTP';
import useSelectedCalendar from  '@/components/hooks/useSelectedCalendar';
import { IloadingType, PlannerSettingsContext } from '@/context/PlannerSettingsContext';
import InputCount from '../planner/InputCount';
import useDebounce from  '@/components/hooks/useDebounce';
import { ColourPicker } from 'thrive-components';

const SettingsSection = ({add}:{add:boolean}) => {
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [color, setColor] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [formValid, setFormValid] = useState<boolean>(false);
    const [formTouched, setFormTouched] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [hexError, setHexError] = useState<string>("");

    const submitting = useRef<boolean>(false);

    const navigate = useNavigate();

    const { post, remove, put } = useHTTP();

    const selectedCalendar = useSelectedCalendar();

    const saveRef = useRef<HTMLButtonElement>(null);

    const {setLoading, setLoadingType, permissions} = useContext(PlannerSettingsContext);

    const debounceColor = useDebounce(color, 500);

    useEffect(() => {        
        if (selectedCalendar && !add){
            setName(selectedCalendar.name)
            setColor(selectedCalendar.hexCode.replace('#', ''))
        }

    }, [selectedCalendar, add])

    useEffect(() => {
        if (add){
            const genRanHex = size => [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
            setColor(genRanHex(6));
        }
    }, [add])
    
    useEffect(() => {

        if (name && debounceColor){
            setFormValid(true)
        }
        else{
            setFormValid(false)
        }

    }, [name, debounceColor])

    useEffect(() => {

        if (error){
            setFormValid(false)
        }

    },[error])

    useEffect(() => {
        // Clear error on type
        setError("")

    }, [name])

    // Validate colour with debounced value
    useEffect(() => {
        
        if (debounceColor && !/^[0-9A-F]{6}$/i.test(debounceColor)){
            setHexError("Invalid hex code")
            setFormValid(false)
        }
        else{
            setHexError("")
        }

    },[debounceColor])

    // No debounce for save button
    useEffect(() => {
        if (color && !/^[0-9A-F]{6}$/i.test(color)){
            setFormValid(false)
        }
    },[color])

    function save(){

        if (saveRef.current){
            saveRef.current.setAttribute("disabled", "disabled");
        }

        setLoadingType(IloadingType.saving)
        setLoading(true)

        add ? create() : edit();
    }

    function create(){

        if (saveRef.current){
            saveRef.current.setAttribute("disabled", "disabled");
        }

        post('planner/calendar', {name: name, hexCode: `#${color}`}).then(() => {

            navigate(`/?saved=calsaved`)

        }).catch(({response}) => {

            if (response.data.message){
                setError(response.data.message)
            }

        }).finally(() =>{
            setLoading(false)
        })
    }

    function edit(){

        put('planner/calendar', {id: selectedCalendar.id, name: name, hexCode: `#${color}`}).then(() => {

            navigate(`/?saved=calsaved`)

        }).catch(({response}) => {

            if (response.data.message){
                setError(response.data.message)
            }

        }).finally(() =>{
            setLoading(false)
        })
    }

    function removeCal(){

        if(!submitting.current){

            setShowConfirmModal(false)

            submitting.current = true;

            setLoading(true)
            setLoadingType(IloadingType.saving)

            remove(`planner/calendar/${selectedCalendar.id}`, {})
            .then(() => {
                navigate(`/?saved=deleted`)
            })
            .catch(({response}) => {
    
                if (response.data.message){
                    setError(response.data.message)
                }  
            }).finally(() => {
                setLoading(false)
            })
        }
    }
   
    return (
        <>
            <div className='planner-settings-content'>
                <h2 className='title'>Settings</h2>

                <div className='settings-form-gen settings-form-overview'>
                    <div className={`cal-name-container ${error ? 'settings-error' : ''}`}>
                        <InputCount 
                            label="Name" 
                            placeholder='Calendar Name' 
                            max={256} 
                            warning={1900} 
                            set={setName} 
                            value={name} 
                            textarea={false}  
                            readOnly={false} 
                            setFormTouched={() => setFormTouched(true)} 
                        />

                        {error && <span>{error}</span>}
                    </div>

                    <ColourPicker 
                        pickerLabel='COLOUR' 
                        setHasError={()=>{}}
                        color={color} 
                        setColor={setColor}                        
                    />
                    {hexError && <span className='error-hex'>{hexError}</span>}
                </div>
            </div>
                        
            <div className='button-container'>
                {!add && permissions.canDeleteCalendar && <button className='delete' onClick={() => setShowConfirmModal(true)}>Delete Calendar</button>}
                <button className='cancel' onClick={() => navigate("/#")}>Cancel</button>
                <button className='save' ref={saveRef} onClick={save} disabled={!formValid || !formTouched}>{add ? 'Create' : 'Save'}</button>
            </div>

            <ModalWrapper hideCloseButton={true} isOpen={showConfirmModal} onRequestClose={() => setShowConfirmModal(false)} className="setting-confirm-modal delete-confirm-modal">
                <p>Are you sure you want to delete this calendar?</p>

                <div className='confirm-buttons'>
                    <button className='return' onClick={() => setShowConfirmModal(false)}>Cancel</button>
                    <button className='discard' onClick={removeCal}>Delete</button>
                </div>
            </ModalWrapper>

        </>
    );
};

export default SettingsSection;
